// TournamentManagePage — admin-only management screen.
// Sections: header (status + actions) → Entry Manager → Seed Manager →
// Generate / Reset bracket → BracketView (with match-result inline modal).
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { CaretLeft, Plus, Trash, ArrowsClockwise, Sparkle, Warning, X, ListChecks, ListNumbers } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { tournamentAPI, TOURNAMENT_STATUS_LABELS } from '../lib/tournamentApi';
import { eventAPI } from '../lib/eventApi';
import BracketView from '../components/tournaments/BracketView';
import MemberLink from '../components/MemberLink';
import EventEditor from '../components/events/EventEditor';
import EventManagerAssignment from '../components/events/EventManagerAssignment';
import CommunicateCard from '../components/events/CommunicateCard';
import AdminRegistrationControlCenter from '../components/events/AdminRegistrationControlCenter';
import ConfirmDialog from '../components/events/ConfirmDialog';
import { FormatOfPlaySelector, FormatAwareScoreFields } from '../components/scoring/FormatOfPlay';
import { localPartsToIso, sameInstant } from '../lib/scheduleTime';

// Renders a tournament entry's name(s) as clickable member link(s).
function ManageEntryName({ entry }) {
  if (!entry) return null;
  const { player1_profile_id, player2_profile_id, player1_name, player2_name, display_name } = entry;
  if (!player2_profile_id && !player2_name) {
    return <MemberLink profileId={player1_profile_id} name={player1_name || display_name} />;
  }
  return (
    <>
      <MemberLink profileId={player1_profile_id} name={player1_name} />
      <span className="text-[#9aa39d]"> / </span>
      <MemberLink profileId={player2_profile_id} name={player2_name} />
    </>
  );
}

// ===================== Player picker =====================
function ProfilePicker({ open, onClose, onPick, excludeIds = [], label = 'Pick a player' }) {
  const [profiles, setProfiles] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    eventAPI.listEligibleProfiles()
      .then((r) => setProfiles(r.data?.profiles || []))
      .catch(() => setProfiles([]));
  }, [open]);

  if (!open) return null;
  const filtered = (profiles || []).filter((p) => {
    if (excludeIds.includes(p.profile_id)) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return p.name.toLowerCase().includes(t) || (p.email || '').toLowerCase().includes(t);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="profile-picker">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b border-[#e3dcd1] flex items-center justify-between">
          <div className="text-sm font-medium text-[#013523]">{label}</div>
          <button onClick={onClose} data-testid="profile-picker-close" className="text-[#6a766e]"><X size={16} /></button>
        </div>
        <div className="p-3 border-b border-[#e3dcd1]">
          <input
            data-testid="profile-picker-search"
            type="text"
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            className="w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {profiles === null && <div className="text-sm text-[#6a766e] p-3">Loading…</div>}
          {profiles !== null && filtered.length === 0 && (
            <div className="text-sm text-[#6a766e] p-3" data-testid="profile-picker-empty">No matches.</div>
          )}
          {filtered.map((p) => (
            <button
              key={p.profile_id}
              data-testid={`profile-picker-item-${p.profile_id}`}
              onClick={() => { onPick(p); }}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#f3f1ec] flex flex-col">
              <span className="text-sm text-[#013523]">{p.name}</span>
              {p.email && <span className="text-[10px] text-[#6a766e]">{p.email}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===================== Entry Manager =====================
function EntryManager({ tournament, onChanged }) {
  const [pickerForSlot, setPickerForSlot] = useState(null); // 'singles' | { team: { player1, player2 }, slot: 1|2 }
  const [pendingTeam, setPendingTeam] = useState(null);  // { player1, player2 } while building a doubles entry
  const isDoubles = tournament.tournament_type === 'doubles';
  const editable = ['draft', 'registration_open', 'registration_closed'].includes(tournament.status);

  const existingProfileIds = useMemo(() => {
    const ids = [];
    tournament.entries.forEach((e) => {
      if (e.player1_profile_id) ids.push(e.player1_profile_id);
      if (e.player2_profile_id) ids.push(e.player2_profile_id);
    });
    return ids;
  }, [tournament.entries]);

  const removeEntry = async (entryId) => {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await tournamentAPI.removeEntry(tournament.id, entryId);
      toast.success('Entry removed.');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not remove entry.');
    }
  };

  const addSinglesEntry = async (profile) => {
    try {
      await tournamentAPI.addEntry(tournament.id, { player1_profile_id: profile.profile_id });
      toast.success(`${profile.name} added.`);
      setPickerForSlot(null);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not add entry.');
    }
  };

  const onPickForDoubles = async (profile) => {
    if (!pendingTeam) {
      // First player picked — open second-pick
      setPendingTeam({ player1: profile });
      setPickerForSlot({ slot: 2 });
      return;
    }
    // Second player picked → submit
    try {
      await tournamentAPI.addEntry(tournament.id, {
        player1_profile_id: pendingTeam.player1.profile_id,
        player2_profile_id: profile.profile_id,
      });
      toast.success(`Team added: ${pendingTeam.player1.name} / ${profile.name}`);
      setPickerForSlot(null);
      setPendingTeam(null);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not add team.');
    }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl p-4 bg-white" data-testid="entry-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[#013523] flex items-center gap-2">
          <ListChecks size={16} /> Entries ({tournament.entries.length})
        </div>
        {editable && (
          <button
            data-testid="add-entry-btn"
            onClick={() => {
              if (isDoubles) {
                setPendingTeam(null);
                setPickerForSlot({ slot: 1 });
              } else {
                setPickerForSlot('singles');
              }
            }}
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1">
            <Plus size={12} /> Add {isDoubles ? 'team' : 'player'}
          </button>
        )}
      </div>
      {!editable && (
        <div className="mb-3 text-xs text-amber-800 bg-amber-50 rounded p-2 border border-amber-200">
          Entries are locked. Reset the bracket to edit entries.
        </div>
      )}
      {tournament.entries.length === 0 && (
        <div className="text-xs text-[#6a766e]" data-testid="entry-manager-empty">No entries yet.</div>
      )}
      <ul className="space-y-1.5">
        {tournament.entries.map((e) => (
          <li key={e.id} className="flex items-center justify-between gap-2 border border-[#f3f1ec] rounded-lg px-3 py-2"
            data-testid={`entry-row-${e.id}`}>
            <div className="text-sm text-[#013523] flex items-center gap-2">
              {e.seed && <span className="text-[10px] text-[#6a766e]">#{e.seed}</span>}
              <span><ManageEntryName entry={e} /></span>
            </div>
            {editable && (
              <button
                data-testid={`remove-entry-${e.id}`}
                onClick={() => removeEntry(e.id)}
                className="text-[#6a766e] hover:text-red-700"
                title="Remove entry">
                <Trash size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>

      <ProfilePicker
        open={!!pickerForSlot}
        onClose={() => { setPickerForSlot(null); setPendingTeam(null); }}
        excludeIds={existingProfileIds}
        label={isDoubles
          ? `Add team — select player ${pendingTeam ? 2 : 1}`
          : 'Add player'}
        onPick={isDoubles ? onPickForDoubles : addSinglesEntry}
      />
    </div>
  );
}

// ===================== Seed Manager =====================
function SeedManager({ tournament, onChanged }) {
  const editable = ['draft', 'registration_open', 'registration_closed'].includes(tournament.status);
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(tournament.entries.map((e) => [e.id, e.seed ?? ''])));
  const [busy, setBusy] = useState(false);

  // Re-sync draft when the entries list changes from outside (after add/remove).
  useEffect(() => {
    setDraft(Object.fromEntries(tournament.entries.map((e) => [e.id, e.seed ?? ''])));
  }, [tournament.entries]);

  const save = async () => {
    // Build payload and validate uniqueness client-side first
    const seenSeeds = new Set();
    const payload = [];
    for (const [entryId, val] of Object.entries(draft)) {
      if (val === '' || val === null) {
        payload.push({ entry_id: entryId, seed: null });
        continue;
      }
      const n = Number(val);
      if (!Number.isInteger(n) || n < 1) {
        toast.error('Seeds must be positive integers (or blank).');
        return;
      }
      if (seenSeeds.has(n)) {
        toast.error(`Seed ${n} is assigned more than once.`);
        return;
      }
      seenSeeds.add(n);
      payload.push({ entry_id: entryId, seed: n });
    }
    setBusy(true);
    try {
      await tournamentAPI.setSeeds(tournament.id, payload);
      toast.success('Seeds saved.');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not save seeds.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl p-4 bg-white" data-testid="seed-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[#013523] flex items-center gap-2">
          <ListNumbers size={16} /> Seeds
        </div>
        {editable && (
          <button data-testid="save-seeds-btn" onClick={save} disabled={busy}
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs disabled:opacity-60">
            {busy ? 'Saving…' : 'Save seeds'}
          </button>
        )}
      </div>
      {tournament.entries.length === 0 ? (
        <div className="text-xs text-[#6a766e]">No entries yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {tournament.entries.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-2"
              data-testid={`seed-row-${e.id}`}>
              <span className="text-sm text-[#013523] truncate"><ManageEntryName entry={e} /></span>
              <input
                data-testid={`seed-input-${e.id}`}
                type="number"
                min="1"
                disabled={!editable}
                placeholder="—"
                value={draft[e.id] ?? ''}
                onChange={(ev) => setDraft({ ...draft, [e.id]: ev.target.value })}
                className="w-16 text-center text-sm px-2 py-1 border border-[#e3dcd1] rounded-lg" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ===================== Match score entry =====================
function MatchResultDialog({ open, match, tournament, tournamentId, onClose, onSaved }) {
  const [winnerId, setWinnerId] = useState(null);
  const [scoreText, setScoreText] = useState('');
  const [scoreValid, setScoreValid] = useState(true);
  const structured = !!tournament?.scoring_format;
  // Phase T-UI3: per-match schedule fields. Stored as separate date + time +
  // court inputs so admins can fill them in piecemeal; combined into a single
  // ISO timestamp before hitting the backend.
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [courtLabel, setCourtLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (open && match) {
      setWinnerId(match.winner_entry_id || null);
      setScoreText(match.score_text || '');
      setScoreValid(true);
      // Split match.scheduled_at into date + time inputs.
      if (match.scheduled_at) {
        const d = new Date(match.scheduled_at);
        if (!Number.isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          setScheduleDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
          setScheduleTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
        } else {
          setScheduleDate(''); setScheduleTime('');
        }
      } else {
        setScheduleDate(''); setScheduleTime('');
      }
      setCourtLabel(match.court_label || '');
    }
  }, [open, match]);

  if (!open || !match) return null;
  const e1 = match.entry1, e2 = match.entry2;
  const canPickWinner = !!(e1 && e2);
  const isBye = match.score_text === '(bye)';
  const onScoreChange = ({ scoreText: st, winnerEntryId, valid }) => {
    setScoreText(st || '');
    setWinnerId(winnerEntryId || null);
    setScoreValid(valid);
  };
  const resultReady = canPickWinner && !isBye && !!winnerId && (!structured || scoreValid);

  // Combine date + time into ISO string (locale time → timestamptz on
  // backend). If neither set, send null to clear.
  const buildScheduledAt = () => localPartsToIso(scheduleDate, scheduleTime);

  const initialSched = match.scheduled_at || null;
  const initialCourt = match.court_label || '';
  const initialDate  = initialSched ? (new Date(initialSched).toISOString().slice(0, 10)) : '';
  const newSched     = buildScheduledAt();
  const scheduleChanged = !sameInstant(newSched, initialSched) || (courtLabel.trim() || '') !== (initialCourt || '');

  const saveSchedule = async () => {
    setBusy(true);
    try {
      await tournamentAPI.updateSchedule(tournamentId, match.id, {
        scheduled_at: newSched || null,
        court_label:  courtLabel.trim() || null,
      });
      toast.success('Schedule saved.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not save schedule.');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const clearSchedule = async () => {
    setBusy(true);
    try {
      await tournamentAPI.updateSchedule(tournamentId, match.id, { scheduled_at: null, court_label: null });
      toast.success('Schedule cleared.');
      setScheduleDate(''); setScheduleTime(''); setCourtLabel('');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not clear schedule.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    // Result + schedule may both have changed. Schedule first (cheap), then result.
    try {
      if (scheduleChanged) {
        await tournamentAPI.updateSchedule(tournamentId, match.id, {
          scheduled_at: newSched || null,
          court_label:  courtLabel.trim() || null,
        });
      }
    } catch {
      // toast already shown via saveSchedule path; abort to let user retry
      return;
    }

    if (resultReady) {
      setBusy(true);
      try {
        await tournamentAPI.recordResult(tournamentId, match.id, {
          winner_entry_id: winnerId,
          score_text: scoreText || null,
        });
        toast.success('Match saved.');
        onSaved();
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.detail?.message || 'Could not save result.');
      } finally {
        setBusy(false);
      }
    } else if (scheduleChanged) {
      toast.success('Schedule saved.');
      onSaved();
      onClose();
    } else {
      onClose();
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await tournamentAPI.clearMatch(tournamentId, match.id);
      toast.success('Match cleared.');
      setConfirmClear(false);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not clear match.');
    } finally {
      setBusy(false);
    }
  };

  // Suppress unused warning — initialDate is only computed to make the
  // schedule-diff logic readable.
  void initialDate; void saveSchedule;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="match-result-dialog">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-[#e3dcd1] sticky top-0 bg-white z-10">
          <div className="text-sm font-medium text-[#013523]">
            Match · R{match.round_number} M{match.match_number}
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* Result section — only available when both entries are set and not a bye */}
          {canPickWinner && !isBye && (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-[#013523]">Result</div>
              {structured ? (
                <FormatAwareScoreFields
                  key={match.id}
                  event={tournament}
                  entry1={e1}
                  entry2={e2}
                  initial={{ winner_entry_id: match.winner_entry_id, score_text: match.score_text }}
                  onChange={onScoreChange}
                />
              ) : (
              <>
              <div className="text-[11px] text-[#6a766e]">Pick the winner.</div>
              {[e1, e2].map((e) => (
                <button
                  key={e.id}
                  data-testid={`pick-winner-${e.id}`}
                  onClick={() => setWinnerId(e.id)}
                  className={`block w-full text-left px-3 py-2 rounded-lg border transition-colors ${winnerId === e.id ? 'bg-[#013523] text-white border-[#013523]' : 'border-[#e3dcd1] text-[#013523]'}`}>
                  {e.seed && <span className="text-[10px] mr-1 opacity-70">#{e.seed}</span>}
                  {e.display_name}
                </button>
              ))}
              <label className="block text-xs text-[#6a766e]">Score (optional)
                <input
                  data-testid="match-score-input"
                  type="text"
                  value={scoreText}
                  onChange={(e) => setScoreText(e.target.value)}
                  placeholder="e.g. 6-4, 6-3"
                  className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
              </label>
              </>
              )}
            </div>
          )}

          {!canPickWinner && !isBye && (
            <div className="text-[11px] text-[#6a766e] bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg px-3 py-2" data-testid="match-result-waiting">
              Both opponents aren&apos;t set yet. You can still schedule this match below — the result form will appear once the prior round is decided.
            </div>
          )}

          {/* Schedule section — always available for non-bye matches */}
          {!isBye && (
            <div className="space-y-2" data-testid="match-schedule-section">
              <div className="text-xs font-medium uppercase tracking-wider text-[#013523]">Schedule</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] text-[#6a766e]">Date
                  <input
                    data-testid="match-schedule-date"
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
                </label>
                <label className="block text-[11px] text-[#6a766e]">Time
                  <input
                    data-testid="match-schedule-time"
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
                </label>
              </div>
              <label className="block text-[11px] text-[#6a766e]">Court
                <input
                  data-testid="match-schedule-court"
                  type="text"
                  value={courtLabel}
                  onChange={(e) => setCourtLabel(e.target.value)}
                  placeholder="e.g. Court 3"
                  className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
              </label>
              {(scheduleDate || scheduleTime || courtLabel) && (
                <button
                  data-testid="match-schedule-clear-btn"
                  type="button"
                  onClick={clearSchedule}
                  disabled={busy}
                  className="text-[11px] text-[#6a766e] underline hover:text-[#013523] disabled:opacity-50">
                  Clear schedule
                </button>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-[#e3dcd1] flex justify-between gap-2 sticky bottom-0 bg-white">
          {canPickWinner && !isBye ? (
            <button data-testid="clear-match-btn" onClick={() => setConfirmClear(true)} disabled={busy}
              className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs disabled:opacity-50">
              Clear result
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button data-testid="match-cancel-btn" onClick={onClose}
              className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-xs text-[#013523]">Cancel</button>
            <button
              data-testid="match-save-btn"
              onClick={submit}
              disabled={busy || (!resultReady && !scheduleChanged)}
              className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs disabled:opacity-60">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Clear this match result?"
        description="Downstream matches (if any) will reset. Standings and any dependent finals will recompute."
        confirmLabel="Clear result"
        destructive
        busy={busy}
        onConfirm={clear}
      />
    </div>
  );
}

// ===================== Bracket controls =====================
function BracketControls({ tournament, onChanged }) {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const canGenerate = ['draft', 'registration_open', 'registration_closed'].includes(tournament.status);
  const canReset = ['bracket_generated', 'in_progress', 'completed'].includes(tournament.status);

  const generate = async () => {
    setBusy(true);
    try {
      await tournamentAPI.generateBracket(tournament.id);
      toast.success('Bracket generated.');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not generate bracket.');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await tournamentAPI.resetBracket(tournament.id);
      toast.success('Bracket reset.');
      setResetConfirm(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not reset bracket.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl p-4 bg-white space-y-3" data-testid="bracket-controls">
      <div className="text-sm font-medium text-[#013523]">Bracket</div>
      <div className="flex flex-wrap gap-2">
        {canGenerate && (
          <button data-testid="generate-bracket-btn" onClick={generate} disabled={busy || tournament.entries.length < 2}
            className="px-3 py-2 rounded-full bg-[#013523] text-white text-sm flex items-center gap-1 disabled:opacity-50">
            <Sparkle size={14} /> {busy ? 'Generating…' : 'Generate bracket'}
          </button>
        )}
        {canReset && (
          <button data-testid="reset-bracket-btn" onClick={() => setResetConfirm(true)} disabled={busy}
            className="px-3 py-2 rounded-full border border-red-300 text-red-700 text-sm flex items-center gap-1">
            <ArrowsClockwise size={14} /> Reset bracket
          </button>
        )}
      </div>
      {tournament.entries.length < 2 && canGenerate && (
        <div className="text-xs text-[#6a766e]">Add at least 2 entries to generate a bracket.</div>
      )}

      {resetConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="reset-bracket-confirm">
          <div className="bg-white rounded-2xl max-w-md w-full p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Warning size={20} weight="fill" className="text-red-700 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium text-[#013523]">Reset bracket?</div>
                <div className="text-xs text-[#6a766e] mt-1">
                  This will permanently delete all matches and recorded results for this tournament.
                  The champion and runner-up will be cleared. Entries and seeds are kept. This cannot be undone.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button data-testid="reset-bracket-cancel" onClick={() => setResetConfirm(false)}
                className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-xs text-[#013523]">Cancel</button>
              <button data-testid="reset-bracket-confirm-btn" onClick={reset} disabled={busy}
                className="px-3 py-1.5 rounded-full bg-red-600 text-white text-xs disabled:opacity-60">
                {busy ? 'Resetting…' : 'Yes, reset bracket'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Status quick-change pill =====================
const NEXT_STATUS = {
  draft: 'registration_open',
  registration_open: 'registration_closed',
  registration_closed: null,  // proceed via Generate bracket
  bracket_generated: null,
  in_progress: null,
  completed: 'archived',
  archived: null,
};

function StatusPill({ tournament, onChanged }) {
  const [busy, setBusy] = useState(false);
  const next = NEXT_STATUS[tournament.status];
  const advance = async () => {
    if (!next) return;
    setBusy(true);
    try {
      await tournamentAPI.update(tournament.id, { status: next });
      toast.success(`Moved to ${TOURNAMENT_STATUS_LABELS[next]}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not change status.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-[#6a766e]">Status:</span>
      <span className="font-medium text-[#013523]" data-testid="tournament-status">
        {TOURNAMENT_STATUS_LABELS[tournament.status]}
      </span>
      {next && (
        <button data-testid="status-advance-btn" onClick={advance} disabled={busy}
          className="text-xs underline text-[#013523]">
          → {TOURNAMENT_STATUS_LABELS[next]}
        </button>
      )}
    </div>
  );
}

// ===================== Page =====================
export default function TournamentManagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEventManager = Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'tournament' && m.event_id === id,
  );
  const canManage = isAdmin || isEventManager;
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  const [activeMatch, setActiveMatch] = useState(null);

  const refresh = () => {
    tournamentAPI.get(id)
      .then((r) => setT(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load.'));
  };
  useEffect(() => { refresh(); }, [id]);

  if (!user) return <div className="text-sm text-[#6a766e]">Loading…</div>;
  if (!canManage) return <Navigate to={`/tournaments/${id}`} replace />;

  if (err) return <div className="text-sm text-red-700">{err}</div>;
  if (!t) return <div className="text-sm text-[#6a766e]" data-testid="manage-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="tournament-manage">
      <div>
        <button
          data-testid="manage-back"
          onClick={() => navigate(`/tournaments/${t.id}`)}
          className="text-xs text-[#6a766e] hover:text-[#013523] flex items-center gap-1">
          <CaretLeft size={14} /> Tournament view
        </button>
        <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#013523]">{t.name}</h1>
          {isAdmin && (
            <a
              href={`/tournaments/${t.id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tournament-manage-view-mode-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#013523] text-white text-xs font-semibold uppercase tracking-[0.14em] hover:bg-[#025a3a] transition-colors">
              View Mode
            </a>
          )}
        </div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <StatusPill tournament={t} onChanged={refresh} />
          {isAdmin && t.status === 'archived' && (
            <button
              data-testid="tournament-restore-btn"
              onClick={async () => {
                try { await tournamentAPI.restore(t.id); toast.success('Event restored.'); refresh(); }
                catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Restore failed'); }
              }}
              className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
              Restore event
            </button>
          )}
        </div>
      </div>

      {/* Event Manager — Admin-only assign / change / remove.
          Placed at the top of the page (immediately below the title
          block) so admins never have to scroll past the entire
          EventEditor + brackets to discover the assignment control.
          Matches the placement used on Club Event and Round Robin
          pages. */}
      <EventManagerAssignment eventKind="tournament" eventId={t.id} isAdmin={isAdmin} />

      {isAdmin && <CommunicateCard eventKind="tournament" eventId={t.id} eventName={t.name} isAdmin={isAdmin} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AdminRegistrationControlCenter eventKind="tournament" event={t} onChanged={refresh} />
        <SeedManager tournament={t} onChanged={refresh} />
      </div>

      <EventEditor
        event={t}
        eventKind="tournament"
        flyerUrl={t.flyer_url}
        onSaved={refresh}
        isAdmin={isAdmin}
        api={{
          updateEvent: (payload) => tournamentAPI.update(t.id, payload),
          uploadFlyer: (file) => tournamentAPI.uploadFlyer(t.id, file),
          deleteFlyer: () => tournamentAPI.deleteFlyer(t.id),
        }}
      />

      <FormatOfPlaySelector
        event={t}
        testidPrefix="t-fop"
        onSave={async (payload) => { await tournamentAPI.update(t.id, payload); refresh(); }}
      />

      <BracketControls tournament={t} onChanged={refresh} />

      {/* Bracket — admin clicks a scheduled/completed match to enter/edit result. */}
      <BracketView
        tournament={t}
        dataTestidPrefix="admin-bracket"
        onMatchClick={(m) => setActiveMatch(m)}
      />

      <MatchResultDialog
        open={!!activeMatch}
        match={activeMatch}
        tournament={t}
        tournamentId={t.id}
        onClose={() => setActiveMatch(null)}
        onSaved={refresh}
      />

      <div className="text-xs text-[#6a766e] mt-8">
        <Link to={`/tournaments/${t.id}`} data-testid="manage-back-to-detail" className="underline">← Public bracket view</Link>
      </div>
    </div>
  );
}
